/**
 * The Occupancy page (POS2-1259) — one site-level view over the daily covers store.
 *
 * CP3 built the read-only shell; CP4 added the write affordances, the week and list views, and
 * the conflict flow. Every write invalidates one query key, because there is one derivation.
 */
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { BaseCoversDrawer } from "@/components/occupancy/base-covers-drawer";
import { CoversChart, type LegendKey } from "@/components/occupancy/covers-chart";
import { DayPanel } from "@/components/occupancy/day-panel";
import { EventDrawer, type DrawerState } from "@/components/occupancy/event-drawer";
import { ListView } from "@/components/occupancy/list-view";
import { MonthView } from "@/components/occupancy/month-view";
import { WeekView } from "@/components/occupancy/week-view";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSitesWithActiveMenus } from "@/hooks/use-active-menus";
import { hasAnyPermission, useClaims } from "@/hooks/use-claims";
import {
  EVENT_TYPES,
  RoomConflictError,
  useBasePattern,
  useDeleteEvent,
  useOccupancyCovers,
  useRemoveOverride,
  useRestoreEvent,
  useSaveBasePattern,
  useSaveEvent,
  useOverrides,
  useSaveOverride,
  useWasteBasis,
  type EventWrite,
  type OccupancyEvent,
} from "@/hooks/use-occupancy";
import { useReportSite } from "@/hooks/use-report-site";
import { useTranslation } from "@/i18n/use-translation";

import {
  applyTypeFilter,
  monthEnd,
  monthKey,
  monthStart,
  shiftMonth,
  weekOf,
} from "@/lib/occupancy";
import { zonedDay } from "@/lib/zoned-day";

const ALL_LEGEND_KEYS: LegendKey[] = ["base", ...EVENT_TYPES];

// Hoisted out of the JSX with its labels attached: an inline `["month", "week", "list"]` reads
// to the i18next rule as user-facing text, and the labels are literal keys because a key built
// at runtime is invisible to `check:i18n`.
const VIEW_MODES = [
  { mode: "month", labelKey: "occupancy.view.month" },
  { mode: "week", labelKey: "occupancy.view.week" },
  { mode: "list", labelKey: "occupancy.view.list" },
] as const;
type ViewMode = (typeof VIEW_MODES)[number]["mode"];

export default function OccupancyPage() {
  const { t } = useTranslation();
  // The shared site-precedence hook: the context switcher's site, then the site last committed
  // on a waste page, then the first alphabetically. Named for Reports, which was its first
  // consumer; the precedence it encodes is not report-specific.
  const { siteId, siteName, timezone, isResolving } = useReportSite();
  const { isPlatformAdmin, permissions } = useClaims();
  // Staff hold occupancy:read and never occupancy:update (decision 7). The API enforces it; this
  // keeps the page from offering an action that would 403.
  //
  // **The platform-admin arm is not optional.** `hasAnyPermission` is a plain array check, and a
  // platform admin carries no org permissions in its claims — it bypasses server-side inside
  // `require_permission`. Without this the whole page renders read-only for the one role that can
  // do everything, which is how it first shipped and what the @smoke walk caught.
  const canWrite = isPlatformAdmin || hasAnyPermission(permissions, ["occupancy:update"]);

  const today = zonedDay(new Date(), timezone);
  const [month, setMonth] = useState(() => monthKey(today));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<Set<LegendKey>>(() => new Set(ALL_LEGEND_KEYS));
  const [view, setView] = useState<ViewMode>("month");
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState<DrawerState>({ mode: "closed" });
  const [baseOpen, setBaseOpen] = useState(false);
  const [clashing, setClashing] = useState<OccupancyEvent[] | null>(null);

  const coversQuery = useOccupancyCovers(siteId, monthStart(month), monthEnd(month));
  const basisQuery = useWasteBasis(siteId);
  const patternQuery = useBasePattern(siteId, today);
  // Only while the drawer is open: it is the only reader, and it is a whole-history query.
  const overridesQuery = useOverrides(siteId, baseOpen);

  const saveEvent = useSaveEvent();
  const deleteEvent = useDeleteEvent();
  const restoreEvent = useRestoreEvent();
  const savePattern = useSaveBasePattern();
  const saveOverride = useSaveOverride();
  const removeOverride = useRemoveOverride();

  // `kitchen_capacity` rides on the site list this hook already holds — a second sites query
  // would be a second cache entry for one number.
  const sitesQuery = useSitesWithActiveMenus();
  const capacity = sitesQuery.data?.find((site) => site.id === siteId)?.kitchen_capacity ?? null;

  const days = useMemo(
    () => (coversQuery.data ?? []).map((day) => applyTypeFilter(day, enabled)),
    [coversQuery.data, enabled],
  );

  const selectedDay = days.find((day) => day.date === selectedDate) ?? null;
  const weekDays = useMemo(
    () => (selectedDate ? weekOf(days, selectedDate) : days.slice(0, 7)),
    [days, selectedDate],
  );

  const toggle = (key: LegendKey) =>
    setEnabled((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const goToday = () => {
    setMonth(monthKey(today));
    setSelectedDate(today);
  };

  const closeDrawer = () => {
    setDrawer({ mode: "closed" });
    setClashing(null);
  };

  /** Save, and hand a 409 to the decision step rather than to a toast. */
  const submit = (body: EventWrite, options?: { allowConflict?: boolean }) => {
    const eventId = drawer.mode === "edit" ? drawer.event.id : undefined;
    saveEvent.mutate(
      { body, eventId, allowConflict: options?.allowConflict },
      {
        onSuccess: (saved) => {
          // Reselect the event's date, so the day panel shows what was just written even when
          // the drawer was opened from somewhere else.
          setSelectedDate(saved.date);
          setMonth(monthKey(saved.date));
          closeDrawer();
          toast.success(t("occupancy.drawer.saved"));
        },
        onError: (error) => {
          if (error instanceof RoomConflictError) {
            setClashing(error.clashing);
            return;
          }
          toast.error(error.message);
        },
      },
    );
  };

  const removeEvent = (event: OccupancyEvent) => {
    deleteEvent.mutate(event.id, {
      onSuccess: () => {
        closeDrawer();
        // Undo, never a confirm dialog: the row is soft-deleted, so restoring it brings back
        // this exact id.
        toast(t("occupancy.drawer.deleted", { name: event.name }), {
          action: {
            label: t("occupancy.action.undo"),
            onClick: () => restoreEvent.mutate(event.id),
          },
        });
      },
    });
  };

  /** Replace is client-orchestrated (decision 6): remove the clash, re-submit, undo restores both. */
  const replaceWith = (body: EventWrite, clash: OccupancyEvent) => {
    deleteEvent.mutate(clash.id, {
      onSuccess: () => {
        saveEvent.mutate(
          { body, eventId: drawer.mode === "edit" ? drawer.event.id : undefined },
          {
            onSuccess: (saved) => {
              setSelectedDate(saved.date);
              closeDrawer();
              toast(t("occupancy.conflict.replaced", { name: clash.name }), {
                action: {
                  label: t("occupancy.action.undo"),
                  onClick: () => {
                    restoreEvent.mutate(clash.id);
                    deleteEvent.mutate(saved.id);
                  },
                },
              });
            },
          },
        );
      },
    });
  };

  const isBusy = saveEvent.isPending || deleteEvent.isPending;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The app's page topbar, the same one Home, Explore and Reports use: title · site on the
          left, controls pushed right, a hairline under it. The page's own content gets the
          padding, so the bar runs full width. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-6 py-3">
        <h1 className="flex min-w-0 items-baseline gap-1.5 font-display text-xl font-extrabold tracking-tight text-gray-900">
          <span className="shrink-0">{t("occupancy.title")}</span>
          {siteName && (
            <>
              <span className="shrink-0 text-gray-300">·</span>
              {/* Long site names truncate with the full name on hover, so the topbar controls
                  never get pushed off or wrapped. */}
              <span className="max-w-[16rem] truncate" title={siteName}>
                {siteName}
              </span>
            </>
          )}
        </h1>
        <div className="flex-1" />
        <div className="flex items-center gap-1 rounded-lg border border-border px-1 py-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t("occupancy.previousMonth")}
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-[104px] text-center text-sm font-semibold tabular-nums">
            {month}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t("occupancy.nextMonth")}
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={goToday}>
          {t("occupancy.today")}
        </Button>
        {canWrite && (
          <>
            <Button variant="outline" size="sm" onClick={() => setBaseOpen(true)}>
              {t("occupancy.action.editBaseCovers")}
            </Button>
            <Button
              size="sm"
              onClick={() => setDrawer({ mode: "add", date: selectedDate ?? today })}
            >
              {t("occupancy.action.addEvent")}
            </Button>
          </>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
        {!isResolving && !siteId ? (
          <p className="text-sm text-muted-foreground">{t("occupancy.empty.noSite")}</p>
        ) : coversQuery.isError ? (
          <p className="text-sm text-destructive">{t("occupancy.error.load")}</p>
        ) : coversQuery.isPending ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-[340px] w-full rounded-xl" />
            <Skeleton className="h-[440px] w-full rounded-xl" />
          </div>
        ) : (
          <>
            <CoversChart
              days={days}
              capacity={capacity}
              enabled={enabled}
              onToggle={toggle}
              onSelectDay={setSelectedDate}
            />

            <div className="flex items-center gap-1 rounded-lg border border-border p-0.5 self-start">
              {VIEW_MODES.map(({ mode, labelKey }) => (
                <Button
                  key={mode}
                  variant={view === mode ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7"
                  onClick={() => setView(mode)}
                >
                  {t(labelKey)}
                </Button>
              ))}
            </div>

            {view === "list" ? (
              <ListView
                days={days}
                search={search}
                onSearchChange={setSearch}
                onEditEvent={canWrite ? (event) => setDrawer({ mode: "edit", event }) : undefined}
                onDeleteEvent={canWrite ? removeEvent : undefined}
              />
            ) : (
              <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
                {view === "month" ? (
                  <MonthView
                    month={month}
                    days={days}
                    capacity={capacity}
                    today={today}
                    selectedDate={selectedDate}
                    onSelectDay={setSelectedDate}
                    onAddForDay={canWrite ? (date) => setDrawer({ mode: "add", date }) : undefined}
                  />
                ) : (
                  <WeekView
                    days={weekDays}
                    capacity={capacity}
                    onEditEvent={
                      canWrite ? (event) => setDrawer({ mode: "edit", event }) : undefined
                    }
                  />
                )}
                {selectedDay && (
                  <DayPanel
                    day={selectedDay}
                    capacity={capacity}
                    basis={basisQuery.data}
                    onEditBase={canWrite ? () => setBaseOpen(true) : undefined}
                    onEditEvent={
                      canWrite ? (event) => setDrawer({ mode: "edit", event }) : undefined
                    }
                    onDeleteEvent={canWrite ? removeEvent : undefined}
                    onAddEvent={
                      canWrite
                        ? () => setDrawer({ mode: "add", date: selectedDay.date })
                        : undefined
                    }
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {siteId && (
        <EventDrawer
          state={drawer}
          siteId={siteId}
          basis={basisQuery.data}
          dayTotal={selectedDay?.total ?? 0}
          clashing={clashing}
          isSaving={isBusy}
          onSave={(body) => submit(body)}
          onReplace={replaceWith}
          onBookBoth={(body) => submit(body, { allowConflict: true })}
          onDismissConflict={() => setClashing(null)}
          onDelete={removeEvent}
          onClose={closeDrawer}
        />
      )}

      {siteId && (
        <BaseCoversDrawer
          open={baseOpen}
          pattern={patternQuery.data}
          overrides={overridesQuery.data?.items ?? []}
          hasMore={overridesQuery.data?.has_more ?? false}
          defaultDate={selectedDate ?? today}
          isSaving={savePattern.isPending || saveOverride.isPending}
          onSavePattern={(covers) =>
            savePattern.mutate(
              {
                site_id: siteId,
                effective_from: today,
                days: Object.entries(covers).map(([weekday, value]) => ({
                  weekday: Number(weekday),
                  covers: value,
                })),
              },
              { onSuccess: () => toast.success(t("occupancy.base.patternSaved")) },
            )
          }
          onSaveOverride={(date, covers) =>
            saveOverride.mutate(
              { site_id: siteId, date, covers, source: "page" },
              {
                onSuccess: () => toast.success(t("occupancy.base.overrideSaved", { date, covers })),
              },
            )
          }
          onRemoveOverride={(date, covers) =>
            removeOverride.mutate(
              { siteId, date },
              {
                // Undo, per the acceptance criteria. A removed override leaves no row to revive,
                // so undo re-writes the same figure rather than clearing a flag — which is why
                // the drawer hands the covers back along with the date.
                onSuccess: () =>
                  // Which it falls back *to* depends on whether the site has a pattern in force.
                  // Promising "back to the weekly pattern" to a site that has none is a plain
                  // untruth: that day now has no base at all.
                  toast(
                    patternQuery.data
                      ? t("occupancy.base.overrideRemoved", { date })
                      : t("occupancy.base.overrideRemovedNoPattern", { date }),
                    {
                      action: {
                        label: t("occupancy.action.undo"),
                        onClick: () =>
                          saveOverride.mutate(
                            { site_id: siteId, date, covers, source: "page" },
                            {
                              onSuccess: () =>
                                toast.success(
                                  t("occupancy.base.overrideRestored", { date, covers }),
                                ),
                            },
                          ),
                      },
                    },
                  ),
              },
            )
          }
          onClose={() => setBaseOpen(false)}
        />
      )}
    </div>
  );
}
